import { Box, Input } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent } from "goi_common";
import { BaseButton, BaseStack } from "goi_common/src/design-system";
import { useCallback, useEffect, useState } from "react";
import { LeadUrgencyStatus } from "@/apis/postLeadV2";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { useGetServerTime } from "@/hooks/useGetServerTime";
import { postLeadConsentRecord } from "@/apis/leadConsentRecord";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { RECIPIENT_DONE, RECIPIENT_SHEET } from "./recipientData";

const PHONE_REGEX = /^010-\d{4}-\d{4}$/;
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const MARKETING_CONSENT_URL = "https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b";
const PRIVACY_CONSENT_URL = "https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d";
const NIGHT_CONSENT_URL = "https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4";

type SheetStep = "form" | "consent" | "done";

interface RecipientBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** 리드 slack_message에 실을 희망 지역 텍스트("시도 시군구"). 미입력 시 빈 문자열. */
  regionText: string;
  /** 준비 상황 원문(장례준비긴급도 텍스트). */
  statusText: string;
  /** postLeadV2 status. 미선택 시 undefined. */
  status?: LeadUrgencyStatus;
}

/**
 * 기초수급자 랜딩 바텀시트(신규). 전화번호 + 개인정보 동의 → postLeadV2 리드 제출
 * → 마케팅 수신동의 화면(선택) → 완료 화면. (무빈소 리드 모달과 동일한 v2 리드 흐름)
 */
export default function RecipientBottomSheet({
  isOpen,
  onClose,
  regionText,
  statusText,
  status,
}: RecipientBottomSheetProps) {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<SheetStep>("form");
  const [entered, setEntered] = useState(false);
  const { submitLead } = useSubmitLead();
  // 야간이면 긴급 수신 동의 고지를 노출하고, 제출(="동의하고 안내받기") 시 accept_night_call=true 전달.
  const { data: serverTime } = useGetServerTime();
  const isNight = serverTime?.is_night ?? false;

  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return;
    }
    const t = setTimeout(() => setEntered(true), 10);
    return () => clearTimeout(t);
  }, [isOpen]);

  const submitEnabled = PHONE_REGEX.test(phone) && !loading;

  const handleClose = useCallback(() => {
    if (loading) return;
    customEvent({ newGtm: { path: "beneficiary", trigger_and_target: "sheet_close" } });
    onClose();
    // 닫힘 후 상태 초기화.
    setTimeout(() => {
      setStep("form");
      setPhone("");
    }, 240);
  }, [loading, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!submitEnabled) return;
    customEvent({
      newGtm: { path: "beneficiary", trigger_and_target: "sheet_submit", text: phone },
    });
    setLoading(true);
    try {
      await submitLead({
        phone,
        lead_type: "home",
        // 제목 + 라벨 뒤 실제 답변은 Slack bold(*...*) 처리.
        slack_message: [
          "*[기초생활수급자 장례 문의]*",
          "",
          "기초생활수급자 장례 관련 검색 후 리드를 제출한 고객입니다.",
          "가능한 혜택과 예상 비용을 설명드리고, 무빈소 희망시 안내 부탁드립니다.",
          "",
          `희망 지역: *${regionText || "미입력"}*`,
          `준비 상황: *${statusText || "미입력"}*`,
        ].join("\n"),
        status,
        // 야간엔 긴급 수신 동의 고지를 노출하고 "동의하고 안내받기" 제출로 동의 → true 전달.
        accept_night_call: isNight ? true : undefined,
      });
      customEvent({ newGtm: { path: "beneficiary", trigger_and_target: "sheet_success" } });
      setStep("consent");
    } catch (e) {
      console.error("리드 제출 실패:", e);
      alert("잠시 오류가 발생했습니다. 문제가 지속될 시 고객센터(1666-9784)로 연락주세요.");
    } finally {
      setLoading(false);
    }
  }, [submitEnabled, phone, regionText, statusText, status, isNight, submitLead]);

  const handleMarketingAgree = useCallback(() => {
    postLeadConsentRecord({ phone, agreement: { marketing_agreed: true } }).catch((e) =>
      console.error("마케팅 수신동의 기록 실패:", e),
    );
    setStep("done");
  }, [phone]);

  if (!isOpen) return null;

  // 완료 화면 — 시트 위 전체를 덮는 흰 화면(README 3)
  if (step === "done") {
    return (
      <Box
        position="fixed"
        inset="0"
        zIndex={1200}
        bg={colors.white}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="12px"
        px="32px"
      >
        <Box
          width="44px"
          height="44px"
          borderRadius="50%"
          bg={colors.orange50}
          display="flex"
          alignItems="center"
          justifyContent="center"
          className="subtitle_18_sb"
          color={colors.orange650}
        >
          ✓
        </Box>
        <Box className="subtitle_18_sb" color={colors.gray900}>
          {RECIPIENT_DONE.title}
        </Box>
        <Box className="body_16_r" color={colors.gray700} textAlign="center">
          {RECIPIENT_DONE.body}
        </Box>
        <Box mt="8px" width="100%" maxWidth="320px">
          <BaseButton type="assistive_2" size="large" onClick={handleClose}>
            <Box className="body_16_m">확인</Box>
          </BaseButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      position="fixed"
      inset="0"
      zIndex={1200}
      bg="rgba(20,20,20,0.45)"
      display="flex"
      alignItems="flex-end"
      onClick={handleClose}
    >
      <BaseStack
        width="100%"
        maxWidth="480px"
        margin="0 auto"
        bg={colors.white}
        borderRadius="18px 18px 0 0"
        padding="22px 20px 20px"
        spacing={16}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={{
          transform: entered ? "translateY(0)" : "translateY(100%)",
          transition: `transform 240ms ${EASING}`,
        }}
      >
        {step === "consent" ? (
          <>
            <BaseStack width="100%" spacing={12} alignItems="center">
              <Box className="subtitle_21_b" color={colors.gray900} textAlign="center">
                신청이 완료되었어요
              </Box>
              <Box className="body_15_r" color={colors.gray700} textAlign="center">
                마케팅 정보 수신에 동의하시면
                <br />
                장례 관련 혜택·이벤트 소식을 보내드려요.
              </Box>
            </BaseStack>
            <BaseStack
              direction="horizontal"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <BaseStack direction="horizontal" spacing={4} alignItems="center">
                <Box className="caption_12_b" color={colors.gray400}>
                  선택
                </Box>
                <Box className="caption_12_b" color={colors.gray700}>
                  마케팅 정보 수신 동의
                </Box>
              </BaseStack>
              <a
                className="caption_12_r"
                style={{ color: colors.gray500, textDecoration: "underline" }}
                href={MARKETING_CONSENT_URL}
                target="_blank"
                rel="noreferrer"
              >
                보기
              </a>
            </BaseStack>
            <BaseStack direction="horizontal" width="100%" spacing={8}>
              <Box flex={1}>
                <BaseButton type="assistive_2" size="large" onClick={() => setStep("done")}>
                  <Box className="body_16_m">닫기</Box>
                </BaseButton>
              </Box>
              <Box flex={1}>
                <BaseButton type="primary" size="large" onClick={handleMarketingAgree}>
                  <Box className="body_16_m" fontWeight={700}>
                    동의하기
                  </Box>
                </BaseButton>
              </Box>
            </BaseStack>
          </>
        ) : (
          <>
            <BaseStack
              direction="horizontal"
              width="100%"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={8}
            >
              <Box className="subtitle_18_sb" color={colors.gray900} lineHeight={1.4}>
                {RECIPIENT_SHEET.title}
                <Box as="span" color={colors.orange650}>
                  {RECIPIENT_SHEET.titleFree}
                </Box>
              </Box>
              <Box cursor="pointer" flexShrink={0} onClick={handleClose}>
                <ColorIcon name="close" width={26} height={26} color={colors.gray900} />
              </Box>
            </BaseStack>

            <Box className="body_15_r" color={colors.gray700} whiteSpace="pre-line">
              {RECIPIENT_SHEET.desc}
            </Box>

            <Input
              type="tel"
              inputMode="tel"
              placeholder={RECIPIENT_SHEET.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              height="50px"
              className="body_16_r"
              border={`1.5px solid ${colors.gray200}`}
              borderRadius="10px"
              focusBorderColor={colors.orange650}
            />

            {/* 개인정보 동의 — 별도 체크박스 없이 필수 고지 + 보기 링크. 제출("동의하고 안내받기")이 동의. */}
            <BaseStack
              direction="horizontal"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              spacing={8}
            >
              <Box as="span" className="caption_13_r" color={colors.gray700}>
                <Box as="span" fontWeight={700} color={colors.gray900}>
                  {RECIPIENT_SHEET.consentRequired}
                </Box>{" "}
                {RECIPIENT_SHEET.consentLabel}
              </Box>
              <a
                className="caption_12_r"
                style={{ color: colors.gray500, textDecoration: "underline", flexShrink: 0 }}
                href={PRIVACY_CONSENT_URL}
                target="_blank"
                rel="noreferrer"
              >
                보기
              </a>
            </BaseStack>

            {/* 야간에만 노출되는 긴급 수신 동의 고지(무빈소와 동일). */}
            {isNight && (
              <BaseStack
                direction="horizontal"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                spacing={8}
              >
                <Box as="span" className="caption_13_r" color={colors.gray700}>
                  <Box as="span" fontWeight={700} color={colors.gray900}>
                    {RECIPIENT_SHEET.nightConsentRequired}
                  </Box>{" "}
                  {RECIPIENT_SHEET.nightConsentLabel}
                </Box>
                <a
                  className="caption_12_r"
                  style={{ color: colors.gray500, textDecoration: "underline", flexShrink: 0 }}
                  href={NIGHT_CONSENT_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  보기
                </a>
              </BaseStack>
            )}

            <Box width="100%">
              <BaseButton
                type="primary"
                size="large"
                disabled={!submitEnabled}
                onClick={handleSubmit}
              >
                <Box className="body_16_m" fontWeight={700}>
                  {RECIPIENT_SHEET.submit}
                </Box>
              </BaseButton>
            </Box>
          </>
        )}
      </BaseStack>
    </Box>
  );
}
